import { useState, useEffect, useCallback, useRef } from 'react'
import { BRAND } from '../../config/brand'

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT'
type Point = { x: number; y: number }

const GRID = 16
const CELL = 20
const TICK_MS = 150
const TREATS = ['🥜', '🍌', '🍎', '🌿', '🥕', '🍉']

function randomTreat() {
 return TREATS[Math.floor(Math.random() * TREATS.length)]
}

function randomPos(avoid: Point[]): Point {
 let p: Point
 do {
 p = { x: Math.floor(Math.random() * GRID), y: Math.floor(Math.random() * GRID) }
 } while (avoid.some(a => a.x === p.x && a.y === p.y))
 return p
}

export function HortonElephantGame() {
 const [playing, setPlaying] = useState(false)
 const [snake, setSnake] = useState<Point[]>([{ x: 8, y: 8 }])
 const [dir, setDir] = useState<Direction>('RIGHT')
 const [food, setFood] = useState<Point>({ x: 12, y: 8 })
 const [foodEmoji, setFoodEmoji] = useState('🥜')
 const [score, setScore] = useState(0)
 const [best, setBest] = useState(0)
 const [dead, setDead] = useState(false)

 const dirRef = useRef(dir)
 const canvasRef = useRef<HTMLDivElement>(null)

 useEffect(() => { dirRef.current = dir }, [dir])

 const reset = useCallback(() => {
 const start = [{ x: 8, y: 8 }]
 setSnake(start)
 setDir('RIGHT')
 dirRef.current = 'RIGHT'
 setFood(randomPos(start))
 setFoodEmoji(randomTreat())
 setScore(0)
 setDead(false)
 setPlaying(true)
 }, [])

 useEffect(() => {
 if (!playing || dead) return

 const handleKey = (e: KeyboardEvent) => {
 const cur = dirRef.current
 switch (e.key) {
 case 'ArrowUp': case 'w': if (cur !== 'DOWN') setDir('UP'); e.preventDefault(); break
 case 'ArrowDown': case 's': if (cur !== 'UP') setDir('DOWN'); e.preventDefault(); break
 case 'ArrowLeft': case 'a': if (cur !== 'RIGHT') setDir('LEFT'); e.preventDefault(); break
 case 'ArrowRight': case 'd': if (cur !== 'LEFT') setDir('RIGHT'); e.preventDefault(); break
 }
 }
 window.addEventListener('keydown', handleKey)
 return () => window.removeEventListener('keydown', handleKey)
 }, [playing, dead])

 useEffect(() => {
 if (!playing || dead) return

 const id = setInterval(() => {
 setSnake(prev => {
 const head = prev[0]
 const d = dirRef.current
 const nx = d === 'LEFT' ? head.x - 1 : d === 'RIGHT' ? head.x + 1 : head.x
 const ny = d === 'UP' ? head.y - 1 : d === 'DOWN' ? head.y + 1 : head.y

 if (nx < 0 || nx >= GRID || ny < 0 || ny >= GRID || prev.some(p => p.x === nx && p.y === ny)) {
 setDead(true)
 setPlaying(false)
 setBest(b => Math.max(b, score))
 return prev
 }

 const newHead = { x: nx, y: ny }
 const ate = nx === food.x && ny === food.y
 const newSnake = [newHead, ...prev]
 if (!ate) newSnake.pop()
 if (ate) {
 setScore(s => s + 1)
 setFood(randomPos(newSnake))
 setFoodEmoji(randomTreat())
 }
 return newSnake
 })
 }, TICK_MS)

 return () => clearInterval(id)
 }, [playing, dead, food, score])

 const handleTouch = useCallback((e: React.TouchEvent) => {
 if (!playing || dead) return
 const rect = canvasRef.current?.getBoundingClientRect()
 if (!rect) return
 const tx = e.touches[0].clientX - rect.left - rect.width / 2
 const ty = e.touches[0].clientY - rect.top - rect.height / 2
 const cur = dirRef.current
 if (Math.abs(tx) > Math.abs(ty)) {
 if (tx > 0 && cur !== 'LEFT') setDir('RIGHT')
 else if (tx < 0 && cur !== 'RIGHT') setDir('LEFT')
 } else {
 if (ty > 0 && cur !== 'UP') setDir('DOWN')
 else if (ty < 0 && cur !== 'DOWN') setDir('UP')
 }
 }, [playing, dead])

 if (!playing && !dead) {
 return (
 <div className="flex flex-col items-center justify-center h-full text-center px-4 select-none">
 <div className="text-6xl mb-4 animate-bounce">🐘</div>
 <p className="text-sm text-muted mb-1">
 {BRAND.assistant} is here, ready when you are.
 </p>
 <p className="text-xs text-muted mb-6">
 Chat with {BRAND.assistant} and suggestions will appear here.
 </p>
 <button
 onClick={reset}
 className="px-4 py-2 text-xs font-medium bg-tint-info-fill /40 text-info rounded-lg hover:bg-tint-info-solid transition-colors"
 >
 🎮 Play a quick game while you think
 </button>
 {best > 0 && (
 <p className="text-app-micro text-muted mt-2">Best: {best} treats</p>
 )}
 </div>
 )
 }

 return (
 <div className="flex flex-col items-center justify-center h-full select-none px-4">
 <div className="flex items-center justify-between w-full max-w-xs mb-2">
 <span className="text-xs text-muted font-mono">
 🥜 {score}{best > 0 ? ` · Best: ${best}` : ''}
 </span>
 <button
 onClick={() => { setPlaying(false); setDead(false) }}
 className="text-app-micro text-muted hover:text-secondary"
 >
 ✕ close
 </button>
 </div>

 <div
 ref={canvasRef}
 onTouchStart={handleTouch}
 className="relative border border-soft rounded-lg bg-tint-ok-wash overflow-hidden"
 style={{ width: GRID * CELL, height: GRID * CELL }}
 >
 {snake.map((seg, i) => (
 <div
 key={i}
 className="absolute transition-all duration-100"
 style={{
 left: seg.x * CELL,
 top: seg.y * CELL,
 width: CELL,
 height: CELL,
 fontSize: i === 0 ? 16 : 12,
 lineHeight: `${CELL}px`,
 textAlign: 'center',
 }}
 >
 {i === 0 ? '🐘' : '🐘'}
 </div>
 ))}
 <div
 className="absolute"
 style={{
 left: food.x * CELL,
 top: food.y * CELL,
 width: CELL,
 height: CELL,
 fontSize: 14,
 lineHeight: `${CELL}px`,
 textAlign: 'center',
 }}
 >
 {foodEmoji}
 </div>
 </div>

 {dead && (
 <div className="mt-3 text-center">
 <p className="text-sm text-secondary mb-2">
 The elephant parade ended! {score > 0 ? `${score} treat${score !== 1 ? 's' : ''} collected.` : ''}
 </p>
 <button
 onClick={reset}
 className="px-4 py-1.5 text-xs font-medium bg-tint-info-fill /40 text-info rounded-lg hover:bg-tint-info-solid transition-colors"
 >
 Play again
 </button>
 </div>
 )}

 {playing && (
 <p className="text-app-micro text-muted mt-2">
 Arrow keys or WASD · Swipe on mobile
 </p>
 )}
 </div>
 )
}

export default HortonElephantGame
